// Narration d'une scène : découpage en phrases (une phrase = un énoncé de la
// voix du navigateur = un sous-titre) et durées estimées. Pur.

import type { JrafNarration } from './schema/document';

/** Débit moyen d'une voix française à la vitesse 1 (caractères par seconde). */
export const SPEECH_CHARS_PER_SECOND = 14;
/** Silence entre deux phrases (ms) : la voix du navigateur le marque, le calendrier le compte. */
export const SENTENCE_PAUSE_MS = 450;

const SENTENCE_END = new Set(['.', '!', '?', '…']);
/** Fermants rattachés à la phrase qui vient de finir (« Bravo ! » ; (voir plus bas.)). */
const CLOSERS = new Set(['»', '”', '"', ')', '’', "'"]);
/** Après une espace, seuls les fermants sans ambiguïté (`"` ouvre aussi bien qu'il ferme). */
const SPACED_CLOSERS = new Set(['»', '”', ')']);
const INNER_SPACE = /[ \u00a0\u202f]/;

/**
 * Découpe un texte en phrases. Une phrase se termine par . ! ? … suivi
 * d'une espace (ou de la fin), avec ses fermants (« Bravo ! » reste entier).
 * Jamais à l'intérieur d'une formule `$…$` (ni `$$…$$`), ni dans un nombre
 * décimal écrit avec un point (« 3.5 »).
 */
export function splitSentences(text: string): string[] {
  const out: string[] = [];
  let current = '';
  let inMath = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    current += c;
    if (c === '$' && text[i - 1] !== '\\') {
      if (text[i + 1] === '$') current += text[++i]!; // `$$` : un seul délimiteur
      inMath = !inMath;
      continue;
    }
    if (inMath || !SENTENCE_END.has(c)) continue;
    // ponctuations finales groupées (« ?! », « ... »)
    while (i + 1 < text.length && SENTENCE_END.has(text[i + 1]!)) {
      current += text[++i]!;
    }
    // fermants, éventuellement précédés d'une espace (« Bravo ! »)
    let j = i + 1;
    while (j < text.length && INNER_SPACE.test(text[j]!)) j++;
    const closers = j === i + 1 ? CLOSERS : SPACED_CLOSERS;
    if (j < text.length && closers.has(text[j]!)) {
      while (j + 1 < text.length && CLOSERS.has(text[j + 1]!)) j++;
      current += text.slice(i + 1, j + 1);
      i = j;
    }
    const next = text[i + 1];
    if (next === undefined || /\s/.test(next)) {
      const s = current.trim();
      if (s) out.push(s);
      current = '';
    }
  }
  const rest = current.trim();
  if (rest) out.push(rest);
  return out;
}

/** Texte prononcé : `say` s'il existe, sinon `text`. */
export function spokenText(narration: Pick<JrafNarration, 'text' | 'say'>): string {
  return narration.say ?? narration.text;
}

/** Vrai si le texte contient du LaTeX (`$…$` ou une commande `\…`) qu'une voix lirait mal. */
export function containsMath(text: string): boolean {
  return /\$|\\[a-zA-Z]/.test(text);
}

/** Vrai si les `$` du texte ne se referment pas (formule cassée à l'affichage). */
export function hasUnbalancedDollars(text: string): boolean {
  let n = 0;
  for (let i = 0; i < text.length; i++) if (text[i] === '$' && text[i - 1] !== '\\') n++;
  return n % 2 === 1;
}

export function estimateSpeechMs(text: string, rate = 1): number {
  const chars = text.replace(/\s+/g, ' ').trim().length;
  return Math.round(((chars / SPEECH_CHARS_PER_SECOND) * 1000) / rate);
}

/**
 * Début estimé de chaque phrase (ms depuis le début de la scène) et durée
 * totale, pour la voix du navigateur. Avec une voix enregistrée, les vrais
 * débuts viennent de l'horodatage des mots (lot 6).
 */
export function estimateSentenceTimings(
  narration: Pick<JrafNarration, 'text' | 'say'>,
  rate = 1,
): { startsMs: number[]; totalMs: number } {
  const sentences = splitSentences(spokenText(narration));
  const startsMs: number[] = [];
  let t = 0;
  sentences.forEach((s, i) => {
    startsMs.push(t);
    t += estimateSpeechMs(s, rate) + (i < sentences.length - 1 ? SENTENCE_PAUSE_MS : 0);
  });
  return { startsMs, totalMs: t };
}
